import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { GlobeHemisphereWest, Warning, CheckCircle, Plus, Trash, CircleNotch, Info } from "@phosphor-icons/react";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const ACCOUNT_TYPES = [
  { value: "bank", label: "Bank" },
  { value: "brokerage", label: "Brokerage" },
  { value: "crypto", label: "Crypto" },
  { value: "pension", label: "Pension / Retirement" },
];

function ThresholdMeter({ meter, testId }) {
  const overThreshold = meter.required;
  const barColor = overThreshold ? "bg-rose-500" : meter.pct >= 60 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-md transition-shadow duration-200" data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{meter.form}</p>
          <p className="text-sm font-medium text-slate-900">{meter.label}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider rounded-sm border px-1.5 py-0.5 font-semibold ${overThreshold ? "text-rose-800 bg-rose-50 border-rose-200" : "text-emerald-800 bg-emerald-50 border-emerald-200"}`}>
          {overThreshold ? "Filing required" : "Below threshold"}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-zinc-100 overflow-hidden mb-2">
        <div className={`absolute inset-y-0 left-0 ${barColor} transition-all duration-500`} style={{ width: `${Math.min(100, meter.pct)}%` }} />
        {/* Threshold marker */}
        <div className="absolute inset-y-0 w-0.5 bg-slate-900" style={{ left: `${meter.threshold_usd > 0 ? Math.min(100, (meter.threshold_usd / Math.max(meter.threshold_usd, meter.current_usd)) * 100) : 100}%` }} />
      </div>
      <div className="flex items-center justify-between text-[11px] mb-2">
        <span className="font-mono font-semibold text-slate-900">{money(meter.current_usd)}</span>
        <span className="text-zinc-500">Threshold: <span className="font-mono">{money(meter.threshold_usd)}</span></span>
      </div>
      <p className="text-[10px] text-zinc-500 leading-relaxed flex items-start gap-1"><Info size={10} className="mt-0.5 shrink-0" /> {meter.basis}</p>
    </div>
  );
}

export function InternationalComplianceDrawer({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["international"],
    queryFn: () => api.get("/portal/international").then((r) => r.data),
    enabled: open,
  });

  const [form, setForm] = useState({ institution: "", country: "", account_type: "bank", peak_balance_usd: "", year_end_balance_usd: "", joint: false });
  const add = useMutation({
    mutationFn: () => api.post("/portal/international", {
      ...form,
      peak_balance_usd: parseFloat(form.peak_balance_usd) || 0,
      year_end_balance_usd: form.year_end_balance_usd ? parseFloat(form.year_end_balance_usd) : null,
      year: new Date().getFullYear(),
    }),
    onSuccess: () => {
      toast.success("Foreign account added — thresholds recalculated");
      setForm({ institution: "", country: "", account_type: "bank", peak_balance_usd: "", year_end_balance_usd: "", joint: false });
      qc.invalidateQueries({ queryKey: ["international"] });
    },
    onError: (e) => toast.error(errText(e)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/portal/international/${id}`),
    onSuccess: () => { toast.success("Account removed"); qc.invalidateQueries({ queryKey: ["international"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const anyRequired = data && Object.values(data.thresholds || {}).some((t) => t.required);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="international-drawer">
        <SheetHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-md bg-amber-100 flex items-center justify-center">
              <GlobeHemisphereWest size={16} weight="fill" className="text-amber-700" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-amber-700">International</p>
              <SheetTitle className="font-heading text-lg leading-tight">Cross-border compliance hub</SheetTitle>
            </div>
          </div>
        </SheetHeader>

        {isLoading ? (
          <p className="text-xs text-zinc-400 mt-6 flex items-center gap-1.5"><CircleNotch size={12} className="animate-spin" /> Loading your foreign accounts…</p>
        ) : (
          <div className="mt-5 space-y-5">
            {anyRequired && (
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 flex items-start gap-2" data-testid="international-required-banner">
                <Warning size={16} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-rose-900">Filing action required</p>
                  <p className="text-[11px] text-rose-800">Your foreign holdings exceed at least one federal threshold. Your CPA will file the required forms alongside your return.</p>
                </div>
              </div>
            )}

            <section data-testid="international-thresholds">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Tax year {data?.year} · {data?.joint_filer ? "Joint filer" : "Single filer"}</p>
              <div className="space-y-3">
                <ThresholdMeter meter={data?.thresholds?.fbar || {}} testId="threshold-fbar" />
                <ThresholdMeter meter={data?.thresholds?.fatca_year_end || {}} testId="threshold-fatca-yearend" />
                <ThresholdMeter meter={data?.thresholds?.fatca_peak || {}} testId="threshold-fatca-peak" />
              </div>
            </section>

            <section data-testid="foreign-accounts-list">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Your foreign accounts ({(data?.accounts || []).length})</p>
              {!(data?.accounts || []).length ? (
                <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50/60 px-3 py-4 text-center">
                  <p className="text-xs text-slate-700">No foreign accounts on file for tax year {data?.year}.</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">Add one below to see live threshold tracking.</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {data.accounts.map((a) => (
                    <div key={a.id} data-testid={`foreign-account-${a.id}`} className="flex items-center justify-between rounded-md border border-zinc-200 bg-white px-2.5 py-2 text-xs hover:shadow-sm transition-shadow duration-150">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{a.institution} <span className="text-zinc-400 font-normal">· {a.country}</span></p>
                        <p className="text-[10px] text-zinc-500">{a.account_type} · Peak <span className="font-mono">{money(a.peak_balance_usd)}</span>{a.year_end_balance_usd ? <> · Year-end <span className="font-mono">{money(a.year_end_balance_usd)}</span></> : null}{a.joint ? " · joint" : ""}</p>
                      </div>
                      <button data-testid={`foreign-account-delete-${a.id}`} onClick={() => del.mutate(a.id)} className="text-zinc-400 hover:text-rose-600 transition-colors duration-150"><Trash size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section data-testid="foreign-account-form" className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Add a foreign account</p>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="fa-institution" placeholder="Institution" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} className="h-8 text-xs" />
                <Input data-testid="fa-country" placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="h-8 text-xs" />
                <select data-testid="fa-type" value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="h-8 text-xs border border-zinc-200 rounded-md bg-white px-2">
                  {ACCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                  <input type="checkbox" data-testid="fa-joint" checked={form.joint} onChange={(e) => setForm({ ...form, joint: e.target.checked })} /> Joint filer
                </label>
                <Input data-testid="fa-peak" placeholder="Peak balance USD" type="number" value={form.peak_balance_usd} onChange={(e) => setForm({ ...form, peak_balance_usd: e.target.value })} className="h-8 text-xs" />
                <Input data-testid="fa-year-end" placeholder="Year-end USD (optional)" type="number" value={form.year_end_balance_usd} onChange={(e) => setForm({ ...form, year_end_balance_usd: e.target.value })} className="h-8 text-xs" />
              </div>
              <Button data-testid="fa-add-btn" size="sm" className="w-full mt-2 h-8" disabled={!form.institution || !form.country || !form.peak_balance_usd || add.isPending} onClick={() => add.mutate()}>
                {add.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <Plus size={13} className="mr-1" />} Add account
              </Button>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
